import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { UserRound } from "lucide-react";
import { useRolePermissions } from "../hooks/useRolePermissions";
import { EmptyState } from "../components/agency/v2/states";
import { ROLES } from "../config/roles";
import AgentProfile from "./profile/AgentProfile";
import PlayerProfile from "./profile/PlayerProfile";
import ClubAdminProfile from "./profile/ClubAdminProfile";
import StaffProfile from "./profile/StaffProfile";
import CoachProfile from "./profile/CoachProfile";
import AgencyAdminProfile from "./profile/AgencyAdminProfile";
import { useSelector } from "react-redux";

const Profile = () => {
  const { hasAnyRole } = useRolePermissions();
  const { currentRole } = useSelector((state) => state.auth);


  if (currentRole === ROLES.AgencyAdmin) {
    return <AgencyAdminProfile />;
  } else if (currentRole === ROLES.Agent) {
    return <AgentProfile />;
  } else if (currentRole === ROLES.Player) {
    return <PlayerProfile />;
  } else if (currentRole === ROLES.ClubAdmin) {
    return <ClubAdminProfile />;
  } else if (currentRole === ROLES.Staff) {
    return <StaffProfile />;
  }else if (currentRole === ROLES.Coach) {
    return <CoachProfile />;
  } else if (currentRole === ROLES.Lawyer) {
    // The lawyer profile surface already exists and is routed
    // (routes/index.js:973 -> pages/settings/LawyerProfileSettings). "My
    // Profile" simply never pointed at it, so the menu's first entry dropped
    // lawyers into the fallback below. Redirect rather than mounting the page
    // a second time, so there stays exactly one address for that surface.
    return <Navigate to="/settings/lawyer-profile" replace />;
  }

  // Check if user is a coach - coaches might be identified by checking profile data
  // This will be handled by checking the user's profile endpoint response
  // For now, coaches will use StaffProfile, but we can add CoachProfile routing later
  // if (currentRole === 'Coach' || currentRole === 'coach') {
  //   return <CoachProfile />;
  // }

  // Fallback. It used to say "Unable to determine your user type" to everyone
  // who reached it — which was simply untrue for Organizers: the role IS known
  // (currentRole === "Organizer"), there is just no profile surface for it
  // yet. And the tile was raw system type with no way out, so whoever landed
  // here was stuck; "My Profile" is the FIRST entry of the profile menu.
  //
  // So the copy now tells the truth for each case, and the tile is the same
  // designed EmptyState the 404 page uses (pages/NotFound.js:14-35) — same
  // component, same 520px column, same "Go to Home" link, so there is always a
  // way back.
  const roleIsKnown = Boolean(currentRole);
  return (
    // min-h-full, not min-h-screen: PrivateLayout already owns the viewport
    // height (the same note NotFound.js carries).
    <div className="min-h-full w-full flex items-start justify-center px-4 py-16">
      <div className="w-full" style={{ maxWidth: 520 }}>
        <EmptyState
          icon={<UserRound size={24} strokeWidth={1.8} color="#2563EB" />}
          title={roleIsKnown ? "No profile page yet" : "Profile not available"}
          body={
            roleIsKnown
              ? `Your account works normally — there is just no profile page for the ${currentRole} role yet.`
              : "We could not determine your account type. Please contact support."
          }
          bodyMaxWidth={400}
          cta={
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-lg bg-[#2563EB] px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-[#1D4ED8]"
            >
              Go to Home
            </Link>
          }
        />
      </div>
    </div>
  );
};

export default Profile;
